// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Box, Collapse, FormControlLabel, IconButton, Switch, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ToggleFileFootprintsLayerAction } from '../mapCommonActions';
import FootprintKindRows from './FootprintKindRows';
import stylesDef from './styles';

interface ComponentProps {
  kinds: FileSelectionKind[];
}

// The Show File Extents switch, grown into a small layers card (D78): with 2+
// kinds on the pane a chevron (2D-pane convention: right = collapsed, down =
// expanded) opens the per-kind filter list. The chevron renders whenever 2+
// kinds exist, disabled while the switch is off, so the card keeps one width
// across both states (D87).
export const FootprintsSwitch: React.FC<ComponentProps> = ({ kinds }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const on = useAppSelector((state) => state.map.common.layers.fileFootprints);
  const [expanded, setExpanded] = useState(false);

  // Switch off hides the list; coming back starts collapsed with every kind
  // re-checked (the slice resets the filter on the same toggle).
  useEffect(() => {
    if (!on) setExpanded(false);
  }, [on]);

  const hasKindList = kinds.length >= 2;
  const listAvailable = on && hasKindList;

  return (
    <Box sx={styles.switchCard}>
      <Box sx={styles.switchRow}>
        <FormControlLabel
          sx={styles.switchControl}
          label={<Typography sx={styles.switchLabel}>Show File Extents</Typography>}
          control={(
            <Switch
              size="small"
              color="primary"
              checked={on}
              onChange={() => dispatch(ToggleFileFootprintsLayerAction())}
            />
          )}
        />
        {hasKindList && (
          <IconButton
            size="small"
            disabled={!on}
            aria-label={expanded ? 'Collapse file extents' : 'Expand file extents'}
            sx={styles.chevronButton}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded
              ? <KeyboardArrowDownIcon fontSize="small" />
              : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      <Collapse in={listAvailable && expanded}>
        <FootprintKindRows kinds={kinds} variant="card" />
      </Collapse>
    </Box>
  );
};

export default FootprintsSwitch;
